<template>
  <div class="w-full">
    <div
      class="text-center pb-4 text-2xl font-bold flex justify-center flex-col items-center"
    >
      Efficacy of Medication
      <div class="bg-tertiary h-1 w-10 my-2"></div>
      <div class="font-normal text-base">
        Have you ever wondered how many people are currently on the same medication as you? Or are you concerned about the lack of effectiveness from a new medication that you just started? Use the dashboard below to get your questions answered!
      </div>
    </div>
    <div class="w-full flex flex-wrap justify-around">
      <div
        class="m-2 relative rounded overflow-hidden"
        style="min-width: 300px; max-width: 400px"
      >
        <div
          v-if="isLoading"
          class="w-full h-full absolute top-0 bottom-0 left-0 right-0 flex justify-center items-center bg-primary z-50 text-primary"
        >
          <i class="fas fa-circle-notch fa-spin fa-5x"></i>
        </div>
        <div class="bg-secondary rounded-t p-2">
          <div class="p-2">
            <p class="font-bold">Medication</p>
            <div class="rounded bg-white border border-gray-200 flex text-gray-700 items-center">
                <div class="relative flex-grow">
                  <select
                    class="block appearance-none w-full py-2 px-2 pr-8 leading-tight focus:outline-none focus:border-gray-500"
                    v-model="filters.mol"
                  >
                    <option value="" disabled selected></option>
                    <option
                      v-for="mol in filtersListMol"
                      :key="mol"
                    >
                      {{ mol }}
                    </option>
                  </select>
                  <div
                    class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                  >
                    <i class="fas fa-chevron-down"></i>
                  </div>
                </div>
                <i class="fas fa-times mr-2" @click="filters.mol = null"></i>
              </div>
          </div>
          <div
            class="text-center hover:underline"
            @click="isVisibleFilters = !isVisibleFilters"
          >
            <span v-if="!isVisibleFilters"
              >More filters <i class="fas fa-chevron-down"></i
            ></span>
            <span v-else>Hide filters <i class="fas fa-chevron-up"></i></span>
          </div>
          <div v-if="isVisibleFilters">
            <div class="p-2">
              <p class="font-bold">Pain Intensity</p>
              <div class="rounded bg-white border border-gray-200 flex text-gray-700 items-center">
                <div class="relative flex-grow">
                  <select
                    class="block appearance-none w-full py-2 px-2 pr-8 leading-tight focus:outline-none focus:border-gray-500"
                    v-model="filters.painIntensity"
                  >
                    <option value="" disabled selected></option>
                    <option
                      v-for="painIntensity in filtersListPainIntensity"
                      :key="painIntensity"
                    >
                      {{ painIntensity }}
                    </option>
                  </select>
                  <div
                    class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                  >
                    <i class="fas fa-chevron-down"></i>
                  </div>
                </div>
                <i class="fas fa-times mr-2" @click="filters.painIntensity = null"></i>
              </div>
            </div>
            <div class="p-2">
              <p class="font-bold">Pain Position</p>
              <div class="rounded bg-white border border-gray-200 flex text-gray-700 items-center">
                <div class="relative flex-grow">
                  <select
                    class="block appearance-none w-full py-2 px-2 pr-8 leading-tight focus:outline-none focus:border-gray-500"
                    v-model="filters.painPosition"
                  >
                    <option value="" disabled selected></option>
                    <option
                      v-for="painPosition in filtersListPainPosition"
                      :key="painPosition"
                    >
                      {{ painPosition }}
                    </option>
                  </select>
                  <div
                    class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                  >
                    <i class="fas fa-chevron-down"></i>
                  </div>
                </div>
                <i class="fas fa-times mr-2" @click="filters.painPosition = null"></i>
              </div>
            </div>
            <div class="p-2">
              <p class="font-bold">Number of Symptoms</p>
              <div class="rounded bg-white border border-gray-200 flex text-gray-700 items-center">
                <div class="relative flex-grow">
                  <select
                    class="block appearance-none w-full py-2 px-2 pr-8 leading-tight focus:outline-none focus:border-gray-500"
                    v-model="filters.nbSymptoms"
                  >
                    <option value="" disabled selected></option>
                    <option
                      v-for="nbSymptoms in filtersListNbSymptoms"
                      :key="nbSymptoms"
                    >
                      {{ nbSymptoms }}
                    </option>
                  </select>
                  <div
                    class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                  >
                    <i class="fas fa-chevron-down"></i>
                  </div>
                </div>
                <i class="fas fa-times mr-2" @click="filters.nbSymptoms = null"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-b py-4 relative overflow-hidden">
          <charts-bar-medication
            v-if="!isLoading"
            :filters="filters"
            class="mx-2"
            @hasEnoughData="hasEnoughData = $event"
          />
          <div
            class="flex justify-center text-black text-sm -mt-4 text-center"
            style="padding-left: 4rem; padding-right: 0.7rem"
          >
            <div class="w-1/3 flex flex-col items-center">
              <img class="h-10 w-10" src="@/assets/bad.svg" />Unhelpful
            </div>
            <div class="w-1/3 flex flex-col items-center">
              <img class="h-10 w-10" src="@/assets/mood.svg" />Somewhat helpful
            </div>
            <div class="w-1/3 flex flex-col items-center">
              <img class="h-10 w-10" src="@/assets/smile.svg" />Helpful
            </div>
          </div>
          <div
            class="absolute bg-white top-0 h-full w-full text-gray-900 flex justify-center items-center opacity-75"
            v-if="!hasEnoughData"
          >
            <div class="text-center flex flex-col items-center">
              <img class="h-10 w-10 m-4" src="@/assets/cogwheel.svg" />
              <div>
                Not enough data for this selection to generate an accurate
                report.<br />
                <b>Change your filters</b> to generate a new report.
              </div>
            </div>
          </div>
          <transition name="fade">
            <div v-if="similarMol" class="px-4 text-primary">
              <hr class="my-4">
              <div class="flex items-center">
                <i class="fas fa-info-circle mr-2 fa-2x"></i>
                <div>People are also taking <b><span v-for="(mol, key) in similarMol" :key="key"><span class="hover:underline" @click="filters.mol = mol">{{ mol }}</span><span v-if="key !== similarMol.length - 1">, </span></span></b> molecules with {{ filters.mol }}.</div>
            </div>
            </div>
          </transition>
        </div>
      </div>
      <div class="max-w-md m-2">
        1. Select the medication that you are interested to learn about in the “Medication” filter box. For example, let’s choose “Naproxen”.
        <br><br>2. Click on “More filters” to add more filters if you wish to get results tailored to your profile. Additional filters include “pain intensity”, “pain location” and/or “number of symptoms”.
        <br><br>3. We’ve also included a small note about the medications that are often taken together with the one you selected.
      </div>
    </div>
  </div>
</template>

<script>
import medication from "@/configs/medication_efficacy_explorer.json";
import similarmols from "@/configs/similar_mols.json";
import ChartsBarMedication from "@/components/ChartsBarMedication";
import { mapState, mapActions } from "vuex";

export default {
  name: "Home",
  components: {
    ChartsBarMedication,
  },
  data() {
    return {
      isLoading: true,
      isVisibleFilters: false,
      hasEnoughData: true,
      filters: {
        mol: null,
        painIntensity: null,
        painPosition: null,
        nbSymptoms: null,
      },
      similarmols: similarmols
    };
  },
  props: {},
  watch: {},
  computed: {
    ...mapState(["medication"]),
    similarMol() {
      return this.filters.mol ? this.similarmols[this.filters.mol] : null
    },
    filtersListMol() {
      let filter = Array.from(new Set(this.medication.map((a) => a.mol)));
      return filter.sort();
    },
    filtersListPainIntensity() {
      let filter = Array.from(
        new Set(this.medication.map((a) => a.painintensity))
      );
      return filter.sort();
    },
    filtersListPainPosition() {
      let filter = Array.from(
        new Set(this.medication.map((a) => a.painposition))
      );
      return filter.sort();
    },
    filtersListNbSymptoms() {
      let filter = Array.from(
        new Set(this.medication.map((a) => a.nb_symptoms))
      );
      return filter.sort();
    },
  },
  mounted() {
    if (process.env.NODE_ENV === "production") {
      fetch(
        "https://helpfulness-insights.migrainebuddy.com/medication_efficacy_explorer.json"
      )
        .then((response) => response.json())
        .then((data) => this.saveMedicationData(data))
        .then(() => {
          this.isLoading = false;
        });
    } else {
      this.saveMedicationData(medication);
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions(["saveMedicationData"]),
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
